import {
  forwardRef,
  type ButtonHTMLAttributes,
  type ComponentPropsWithoutRef,
  type ElementType,
  type ForwardedRef,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import { cn } from "@/shared/lib/cn";

export type ButtonVariant =
  | "primary"
  | "dark"
  | "secondary"
  | "ghost"
  | "danger"
  | "inverse";
export type ButtonSize = "sm" | "md" | "lg";
export type ButtonShape = "pill" | "rounded";

/** Lo que este botón añade por su cuenta, sea cual sea el elemento que pinte. */
export interface ButtonOwnProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** `pill` (por defecto) para acciones sueltas; `rounded` dentro de formularios. */
  shape?: ButtonShape;
  /** Deshabilita y muestra el spinner SIN cambiar el tamaño del botón. */
  loading?: boolean;
  fullWidth?: boolean;
  /** Botón cuadrado de puro icono. Exige `aria-label`. */
  iconOnly?: boolean;
  /**
   * Fuerza la animación de NAVEGAR (ver abajo) en un `<button>` que abre
   * otra página por código (`window.open` a WhatsApp, por ejemplo). Los
   * botones pintados como enlace (`as={Link}`) la traen solos.
   */
  nav?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  children?: ReactNode;
  className?: string;
  disabled?: boolean;
}

/**
 * Props del botón. `C` es el elemento que pinta: `"button"` por defecto,
 * o cualquier otro componente vía `as` (típicamente el `Link` del router).
 */
export type ButtonProps<C extends ElementType = "button"> = ButtonOwnProps & {
  /**
   * Con qué elemento se pinta. Existe para que una acción que NAVEGA
   * pueda ser un enlace de verdad —`<Button as={Link} to="/login">`— y no
   * un `<button>` que llama a `navigate()`: así funciona el clic con
   * rueda, el "abrir en pestaña nueva" y el menú contextual, y el lector
   * de pantalla lo anuncia como enlace. La alternativa era un segundo
   * componente `LinkButton` con las clases copiadas, que es exactamente
   * el duplicado que este archivo vino a eliminar.
   */
  as?: C;
} & Omit<ComponentPropsWithoutRef<C>, keyof ButtonOwnProps | "as">;

/**
 * El botón canónico de New Horizons.
 *
 * ── Por qué existe ──────────────────────────────────────────────────
 * Lo migrado traía DOS botones incompatibles: `primitives/Button`
 * (CSS propio `itb-btn`, variantes primary|dark|secondary|ghost, con
 * `iconOnly`/`fullWidth`, sin `loading`) y `ui/Button` (utilities
 * Tailwind `ds-*`, variantes primary|secondary|ghost|danger, con
 * `loading`, sin `fullWidth` ni `iconOnly`). Este los fusiona: la unión
 * de las variantes y la unión de las capacidades.
 *
 * Se eligió la implementación con utilities de Tailwind y no el CSS
 * propio porque `cn()` (twMerge) puede resolver conflictos: quien
 * consuma el botón puede pasar `className="rounded-2xl"` y ganar. Con
 * clases `itb-btn--*` la última definición del archivo CSS mandaba y el
 * override era imposible sin `!important`.
 *
 * ── El spinner no mueve nada ────────────────────────────────────────
 * En `loading` el contenido NO se desmonta: se vuelve invisible y el
 * spinner se superpone en absoluto. Así el ancho del botón es el mismo
 * antes, durante y después — el salto de layout clásico no ocurre.
 */
const variantClasses: Record<ButtonVariant, string> = {
  primary:
    "bg-ds-violet-pop text-white shadow-ds-cta hover:bg-ds-purple hover:shadow-ds-card-hv hover:-translate-y-px active:translate-y-0",
  dark:
    "bg-ds-ink text-white shadow-ds-card hover:bg-ds-ink-2 hover:shadow-ds-card-hv hover:-translate-y-px active:translate-y-0",
  secondary:
    "bg-ds-mist text-ds-purple border border-ds-border hover:bg-ds-lavender-bg-2 hover:text-ds-violet-pop hover:shadow-ds-card",
  ghost:
    "bg-transparent text-ds-ink-3 border border-ds-border-strong hover:bg-ds-mist/60 hover:border-ds-violet-pop hover:text-ds-violet-pop",
  danger:
    "bg-ds-terracotta text-white shadow-ds-card hover:shadow-ds-card-hv hover:-translate-y-px active:translate-y-0",
  /*
   * `inverse` — para SUPERFICIES OSCURAS (el bloque de CTA, un hero en
   * tinta). Ninguna de las cinco anteriores servía ahí: `primary` es
   * violeta sobre casi-negro (contraste pobre), `ghost` hereda un texto
   * gris oscuro que desaparece, y `secondary` trae un fondo lila claro
   * con texto morado. Esta es blanca con texto tinta, que es como se ve
   * el botón del CTA en el sistema real.
   */
  inverse:
    "bg-white text-ds-ink shadow-ds-card hover:bg-ds-cream hover:shadow-ds-card-hv hover:-translate-y-px active:translate-y-0",
};

/**
 * ── La animación de NAVEGAR (referencia "interactive-hover-button",
 *    Anahí 27-ago) ──────────────────────────────────────────────────
 * Todo botón que LLEVA a otra página (un enlace, o `nav` explícito)
 * anima al pasar el cursor: el contenido se desliza y desvanece, un
 * punto crece hasta llenar el botón con el color de ACENTO de su
 * variante, y encima entra el mismo texto con una flecha. En reposo el
 * botón se ve EXACTAMENTE igual que siempre — la referencia puso la
 * estructura, los colores son los nuestros.
 *
 * El punto que crece usa el color que la variante YA usaba al hover
 * (primary→purple, dark→ink-2…); en las claras (secondary/ghost/
 * inverse) crece el violeta firma, que es el acento de la casa.
 */
const navFillClasses: Record<ButtonVariant, string> = {
  primary: "bg-ds-purple",
  dark: "bg-ds-ink-2",
  secondary: "bg-ds-violet-pop",
  ghost: "bg-ds-violet-pop",
  danger: "bg-ds-ink-2",
  inverse: "bg-ds-violet-pop",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 px-3.5 text-[12px] gap-1.5",
  md: "h-10 px-5 text-[13px] gap-2",
  lg: "h-12 px-7 text-[14px] gap-2",
};

const iconOnlySizeClasses: Record<ButtonSize, string> = {
  sm: "h-8 w-8 p-0",
  md: "h-10 w-10 p-0",
  lg: "h-12 w-12 p-0",
};

const spinnerSizeClasses: Record<ButtonSize, string> = {
  sm: "h-3.5 w-3.5",
  md: "h-4 w-4",
  lg: "h-[18px] w-[18px]",
};

function ButtonImpl(
  {
    as,
    variant = "primary",
    size = "md",
    shape = "pill",
    loading = false,
    fullWidth = false,
    iconOnly = false,
    nav = false,
    leftIcon,
    rightIcon,
    disabled,
    className,
    children,
    ...rest
  }: ButtonProps<ElementType>,
  ref: ForwardedRef<HTMLElement>,
) {
  const Element = (as ?? "button") as ElementType;
  const isNativeButton = Element === "button";
  const isDisabled = Boolean(disabled) || loading;
  /* Navega → anima. Un enlace lo es por serlo; un botón, si lo declara.
   * Los de puro icono no: no hay texto que deslizar. */
  const animatesNav = (nav || !isNativeButton) && !iconOnly && !loading && !isDisabled;

  /*
   * `disabled` solo existe en `<button>`. Sobre un enlace hay que apagarlo
   * a mano: sin `aria-disabled` el lector de pantalla lo sigue ofreciendo,
   * y sin sacarlo del orden de tabulación el teclado cae en una acción que
   * no hace nada.
   */
  const stateProps = isNativeButton
    ? {
        type:
          (rest as ButtonHTMLAttributes<HTMLButtonElement>).type ??
          ("button" as const),
        disabled: isDisabled,
      }
    : isDisabled
      ? { "aria-disabled": true, tabIndex: -1 }
      : {};

  return (
    <Element
      ref={ref}
      aria-busy={loading || undefined}
      className={cn(
        "relative inline-flex items-center justify-center whitespace-nowrap font-semibold leading-none no-underline",
        "transition-all duration-200 ease-[cubic-bezier(0.25,0.1,0.25,1)]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-ds-cream",
        "disabled:pointer-events-none disabled:opacity-50 disabled:translate-y-0 disabled:shadow-none",
        "aria-disabled:pointer-events-none aria-disabled:opacity-50",
        "motion-reduce:transform-none motion-reduce:transition-none",
        shape === "pill" ? "rounded-ds-pill" : "rounded-2xl",
        iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
        fullWidth && "w-full",
        variantClasses[variant],
        animatesNav && "group/nav overflow-hidden",
        className,
      )}
      {...rest}
      {...stateProps}
    >
      {/*
        El contenido se queda montado durante `loading` para conservar el
        ancho. `invisible` lo saca del árbol de accesibilidad y del pintado
        sin sacarlo del flujo.
      */}
      <span
        className={cn(
          "inline-flex items-center justify-center gap-[inherit]",
          loading && "invisible",
          animatesNav &&
            "transition-all duration-300 group-hover/nav:translate-x-8 group-hover/nav:opacity-0 motion-reduce:transition-none motion-reduce:group-hover/nav:translate-x-0",
        )}
      >
        {leftIcon && (
          <span className="inline-flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">{leftIcon}</span>
        )}
        {!iconOnly && children}
        {rightIcon && (
          <span className="inline-flex shrink-0 [&>svg]:h-4 [&>svg]:w-4">{rightIcon}</span>
        )}
      </span>

      {animatesNav && (
        <>
          {/* El punto que crece hasta llenar el botón con el acento. */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute left-[20%] top-[40%] z-[1] h-2 w-2 rounded-full transition-all duration-300",
              "group-hover/nav:left-0 group-hover/nav:top-0 group-hover/nav:h-full group-hover/nav:w-full group-hover/nav:scale-[1.8]",
              "motion-reduce:transition-none",
              "opacity-0 group-hover/nav:opacity-100",
              navFillClasses[variant],
            )}
          />
          {/* El mismo texto, entrando con su flecha sobre el acento. */}
          <span
            aria-hidden="true"
            className={cn(
              "absolute inset-0 z-[2] flex -translate-x-8 items-center justify-center gap-2 text-white opacity-0",
              "transition-all duration-300 group-hover/nav:translate-x-0 group-hover/nav:opacity-100",
              "motion-reduce:transition-none motion-reduce:-translate-x-0",
            )}
          >
            {!iconOnly && children}
            <ArrowRight className="h-4 w-4 shrink-0" />
          </span>
        </>
      )}

      {loading && (
        <span className="absolute inset-0 inline-flex items-center justify-center">
          <Loader2
            className={cn("animate-spin motion-reduce:animate-none", spinnerSizeClasses[size])}
            aria-hidden="true"
          />
          <span className="sr-only">Cargando…</span>
        </span>
      )}
    </Element>
  );
}

/*
 * `forwardRef` no conserva los genéricos: su firma fija el tipo de props.
 * El cast de aquí abajo se lo devuelve, para que `<Button as={Link} to="…">`
 * exija y compruebe las props de `Link` — y un `<Button>` normal siga
 * exigiendo las de `<button>`.
 */
export const Button = forwardRef(ButtonImpl) as <C extends ElementType = "button">(
  props: ButtonProps<C> & { ref?: Ref<HTMLElement> },
) => ReactElement | null;
